import React from "react";
import styled, { css } from "styled-components";
import { colors } from "styles/color-tokens";

import SkedIcon from "ui/SkedIcon/SkedIcon";
import { FormSize, formSizeStyles } from "../form-theme-provider";

type CheckBoxProps = {
  children?: React.ReactNode;
  className?: string;
  label?: string;
  onClick?: () => void;
  onLabelClick?: () => void;
  onChange?: (value: boolean) => void;
  checked?: boolean;
  isPartialChecked?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  size?: FormSize;
};

function CheckboxComponent(props: CheckBoxProps) {
  const {
    children,
    className,
    label,
    onClick,
    onChange,
    checked,
    onLabelClick,
    isPartialChecked,
    disabled,
    readOnly = false,
    size = FormSize.medium,
  } = props;

  function handleChange() {
    if (!onChange && onClick) {
      onClick();
    } else {
      onChange?.(!checked);
    }
  }

  return (
    <CheckBoxRoot
      className={className}
      disabled={disabled && !readOnly}
      isPartialChecked={isPartialChecked}
      size={size}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={handleChange}
        disabled={disabled || readOnly}
      />
      {isPartialChecked && <SkedIcon icon="horizontal-check" size="12" />}
      <SkedCheckboxLabel size={size} onClick={onLabelClick}>
        {children || label}
      </SkedCheckboxLabel>
    </CheckBoxRoot>
  );
}

export const SkedCheckboxLabel = styled.div<{ size?: FormSize }>`
  width: 100%;
  font-size: 14px;
  display: flex;
  align-items: center;
  cursor: pointer;

  &:before {
    content: "";
    display: flex;
    justify-content: center;
    align-items: center;
    width: 20px;
    height: 20px;
    line-height: 20px;
    border: 1px solid ${colors.primary500};
    border-radius: 4px;
    color: ${colors.white};
    margin-right: 10px;
    font-size: 12px;
  }

  &:empty &:before {
    margin-right: 0;
  }

  ${formSizeStyles(
    FormSize.small,
    css`
      font-size: 11px;
      line-height: 11px;

      &:before {
        width: 14px;
        height: 14px;
        font-size: 6px;
        border-radius: 3px;
        margin-right: 5px;
      }
    `
  )}
`;

const CheckBoxRoot = styled.label<{
  disabled?: boolean;
  isPartialChecked?: boolean;
  size?: FormSize;
}>`
  cursor: pointer;
  user-select: none;
  margin-bottom: 0;

  ${({ isPartialChecked }) =>
    isPartialChecked &&
    `
      display: grid;
      position: relative;
      place-items: center;

      span.SkedIcon {
        position: absolute;
        left: 4px;
      }
  `}

  input[type="checkbox"] {
    display: block;
    width: 0;
    height: 0;
    opacity: 0;
    clip: rect(0, 0, 0, 0);
    margin: 0;
  }

  input[type="checkbox"]:checked + ${SkedCheckboxLabel}:before {
    content: "";
    background: ${colors.primary500};
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath d='m3%208%203%203%207-7' fill='none' stroke='white' stroke-linecap='round' stroke-linejoin='round' stroke-width='2'/%3E%3C/svg%3E");
    background-position: center;
    background-repeat: no-repeat;
    background-size: 70% 70%;
    border: 1px solid ${colors.primary500};
  }

  ${({ disabled }) =>
    disabled &&
    css`
      opacity: 0.3;
      pointer-events: none;
    `}
`;

export default styled(CheckboxComponent)``;

export const Checkbox = styled(CheckboxComponent)``;
